import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { FormInputContainer } from 'blaze-form';
import { Button, FormCheck, Badge, FormControl } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import ApiService from '../../services/ApiService';
import { statusColors, getStatus } from '../../util/vehicle';
import MaterialSearch from '../MaterialSearch';
import FileNotFound from '../FileNotFound';
import CurrencyItem from '../form/misc/CurrencyItem';

// TODO Grab from AVT1 ?
const types = [
  'APU',
  'AUTO',
  'BOAT',
  'CAB',
  'CTLR',
  'EQP',
  'FLB',
  'LOT',
  'OTW',
  'REF',
  'RFT',
  'SCT',
  'TLR',
  'TRK',
  'TRX',
  'UD',
  'YRD',
];

const UnitListSearch = ({ onSearch }) => {
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState('');

  const handleChange = event => {
    setTerms(event.target.value);
  };

  const handleClear = () => {
    setTerms('');
  };

  const searchCallback = () => {
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    onSearch(terms, searchCallback);
  }, [terms]);

  return (
    <MaterialSearch loading={loading} showResults={false} onChange={handleChange} onClear={handleClear} value={terms} />
  );
};

const UnitListItem = ({ unit, onCheck, onUncheck, initial = false }) => {
  const [checked, setChecked] = useState(initial);

  const handleRowClick = () => {
    setChecked(prev => !prev);
  };

  const status = getStatus(unit.Status);
  const condition = getStatus(unit['New-Used']);

  useEffect(() => {
    if (checked) {
      onCheck(unit.itemid);
    } else {
      onUncheck(unit.itemid);
    }
  }, [checked]);

  return (
    <tr className="unit-list-item" onClick={handleRowClick}>
      <td width="15">
        <FormCheck className="pl-1">
          <FormCheck.Input type="checkbox" onChange={() => {}} checked={checked} />
        </FormCheck>
      </td>
      <td>
        <Badge
          title={unit.Status}
          className="mr-1 ml-1"
          style={{ color: '#fff', padding: '3x 5px', backgroundColor: statusColors[status] || '#000' }}
        >
          {status || '?'}
        </Badge>
      </td>
      <td>
        <div className="unit-id" title={unit.itemid}>
          {unit.itemid}
        </div>
      </td>
      <td>{unit.Type}</td>
      <td>
        <Badge
          title={unit['New-Used']}
          className="mr-1 ml-1"
          style={{ color: '#fff', padding: '3x 5px', backgroundColor: statusColors[condition] || '#000' }}
        >
          {condition || '?'}
        </Badge>
      </td>
      <td>{unit.Year}</td>
      <td className="unit-make" title={`${unit.Make} ${unit.Model}`}>
        {unit.Make} {unit.Model}
      </td>
      <td title="Total Cost" className="text-right">
        <CurrencyItem>{unit['Total Cost']}</CurrencyItem>
      </td>
      <td title={`PO ${unit['PO Number']}`}>
        <Badge>{unit['PO Number']}</Badge>
      </td>
    </tr>
  );
};

const FormMultiUnitSelection = ({ onChange, field, ...props }) => {
  const [key, setKey] = useState(0);
  const [termsKey, setTermsKey] = useState(0);
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [userUnits, setUserUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [fetchingUnits, setFetchingUnits] = useState(false);
  const [currentType, setCurrentType] = useState('');
  const [currentCond, setCurrentCond] = useState('');

  const lastTerms = useRef('');

  const onCheck = unitId => {
    setSelectedUnits([...new Set([unitId, ...selectedUnits])]);
  };

  const onUncheck = unitId => {
    setSelectedUnits(selectedUnits.filter(id => id !== unitId));
  };

  const handleConfirm = () => {
    setUserUnits([...new Set([...userUnits, ...selectedUnits])]);
    setSelectedUnits([]);
  };

  const handleClear = () => {
    setSelectedUnits([]);
    incrementKey();
  };

  const handleTypeChange = event => {
    setCurrentType(event.target.value);
  };

  const handleCondChange = event => {
    setCurrentCond(event.target.value);
  };

  const removeTag = unitId => {
    setUserUnits(userUnits.filter(id => id !== unitId));
  };

  const incrementKey = () => {
    setKey(prev => prev + 1);
  };

  const incrementTermsKey = () => {
    setTermsKey(prev => prev + 1);
  };

  const fetchUnits = qs => {
    setFetchingUnits(true);
    return ApiService.get('crm/units', { params: { qs } })
      .then(response => {
        setUnits(response.data.list);
      })
      .finally(() => {
        setFetchingUnits(false);
      });
  };

  const handleSearch = (terms, callback) => {
    if (terms !== lastTerms.current) {
      lastTerms.current = terms;
      fetchUnits(terms).then(callback);
      return;
    }

    callback(true);
  };

  const handleCheckAll = () => {
    // Check for uncheckall
    if (filteredUnits.length === selectedUnits.length) {
      handleClear();
      return;
    }

    setSelectedUnits(filteredUnits.map(u => u.itemid));
    incrementKey();
  };

  const handleClearFilters = () => {
    incrementTermsKey();
    setCurrentType('');
    setCurrentCond('');
  };

  const renderUnitList = () => {
    if (fetchingUnits && units.length === 0) {
      return (
        <div style={{ fontSize: '14px' }}>
          {[1, 2, 3, 4, 5, 6, 7].map((v, i) => (
            <Skeleton key={i} style={{ lineHeight: '1.5' }} />
          ))}
        </div>
      );
    }

    if (!filteredUnits.length) {
      return <FileNotFound>No Matching Units</FileNotFound>;
    }

    return (
      <table>
        <tbody>
          {filteredUnits.map(unit => {
            return (
              <UnitListItem
                key={`${unit.itemid}-${key}`}
                unit={unit}
                onCheck={onCheck}
                onUncheck={onUncheck}
                initial={selectedUnits.includes(unit.itemid)}
              />
            );
          })}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    onChange(field.name, userUnits);
  }, [userUnits]);

  useEffect(() => {
    setFilteredUnits(
      units.filter(unit => {
        // Filter Units already confirmed
        if (userUnits.includes(unit.itemid)) {
          return false;
        }

        // Filter Type
        if (currentType && unit.Type !== currentType) {
          return false;
        }

        // Filter Condition
        if (currentCond && unit['New-Used'] !== currentCond) {
          return false;
        }

        return true;
      })
    );
  }, [currentType, userUnits, units, currentCond]);

  useEffect(() => {
    // Everytime a filter gets applied need to make sure selected unit is still on the list
    if (selectedUnits.length) {
      setSelectedUnits(prev => {
        return prev.filter(unitId => {
          if (filteredUnits.find(fu => fu.itemid === unitId)) {
            return true;
          }

          return false;
        });
      });
    }
  }, [filteredUnits]);

  useEffect(() => {
    fetchUnits();
  }, []);

  return (
    <FormInputContainer {...props} field={field}>
      <div className="unit-list-toolbar">
        <Button
          variant="secondary"
          size="xs"
          onClick={handleCheckAll}
          title="Check All"
          disabled={!filteredUnits.length}
        >
          <i className="fas fa-check-double" />
        </Button>
        <UnitListSearch key={`t${termsKey}`} onSearch={_.debounce(handleSearch, 500)} />
        <div className="form-select mb-0 ml-1">
          <FormControl
            onChange={handleTypeChange}
            as="select"
            value={currentType}
            style={{ fontSize: '11px', height: '27px' }}
          >
            <option key="empty" value="">
              Type
            </option>
            {types.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </FormControl>
        </div>
        <div className="form-select mb-0 ml-1">
          <FormControl
            onChange={handleCondChange}
            as="select"
            value={currentCond}
            style={{ fontSize: '11px', height: '27px' }}
          >
            <option key="empty" value="">
              Cond
            </option>
            {['New', 'Used'].map(cond => (
              <option key={cond} value={cond}>
                {cond}
              </option>
            ))}
          </FormControl>
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={handleClearFilters}
          title="Clear filters"
          disabled={filteredUnits.length === units.length}
        >
          <i className="fas fa-times-octagon" />
        </Button>
      </div>
      <div className="unit-list">{renderUnitList()}</div>
      <div className="unit-select-tools">
        <div className="confirm-units">
          <Button size="sm" variant="success" onClick={handleConfirm} disabled={selectedUnits.length === 0}>
            {selectedUnits.length > 0 ? (
              <>
                <i className="fas fa-chevron-double-down" />
                <span>
                  Confirm {selectedUnits.length} Unit
                  {selectedUnits.length === 1 ? '' : 's'}
                </span>
                <i className="fas fa-chevron-double-down" />
              </>
            ) : (
              <div className="flex-grow-1">
                <i className="fas fa-level-up fa-flip-horizontal float-left" style={{ paddingTop: '2px' }} />
                Select units from the list above
              </div>
            )}
          </Button>
        </div>
        {selectedUnits.length > 0 && (
          <Button
            size="sm"
            variant="danger"
            style={{ backgroundColor: '#ccc', marginLeft: '5px' }}
            onClick={handleClear}
            disabled={selectedUnits.length === 0}
          >
            <i className="fas fa-times" />
          </Button>
        )}
      </div>
      <div className="user-list">
        {userUnits.length === 0 && (
          <div className="empty-user-list">
            <div className="warn-sect">
              <div>
                <i className="fad fa-exclamation-triangle"></i> No units have been confirmed
              </div>
            </div>
          </div>
        )}
        {userUnits.map(unit => {
          return (
            <div className="unit-tag-wrapper" key={unit}>
              <div className="unit-tag">
                <div className="label">{unit}</div>
                <i className="fas fa-times-circle" onClick={() => removeTag(unit)} />
              </div>
            </div>
          );
        })}
        {userUnits.length > 0 && (
          <div className="total-units">
            {userUnits.length} Unit{userUnits.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </FormInputContainer>
  );
};

export default FormMultiUnitSelection;
